import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import {
  Bell,
  BellOff,
  Download,
  Smartphone,
  Wifi,
  WifiOff,
  CheckCircle2,
  XCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  requestNotificationPermission,
  getNotificationPermission,
  showNotification,
  isInstalledAsPWA,
  canPromptInstall,
  promptInstall,
  supportsNotifications,
  subscribeToPushNotifications,
} from "../utils/pwa";
import { useAuth } from "../context/AuthContext";

export default function PWASettings() {
  const { user } = useAuth();
  const [notificationPermission, setNotificationPermission] = useState("default");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInstalled, setIsInstalled] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [swRegistered, setSwRegistered] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Verificar estado inicial
    setNotificationPermission(getNotificationPermission());
    setIsInstalled(isInstalledAsPWA());
    setCanInstall(canPromptInstall());
    
    // Verificar service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setSwRegistered(true);
      });
    }
    
    // Listeners para estado online/offline
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Listener para prompt de instalação
    const handleInstallPrompt = () => setCanInstall(true);
    window.addEventListener('beforeinstallprompt', handleInstallPrompt);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
    };
  }, []);

  const handleRequestPermission = async () => {
    const granted = await requestNotificationPermission();
    setNotificationPermission(getNotificationPermission());
    
    if (granted) {
      toast.success("Notificações ativadas!");
      
      // Subscrever automaticamente
      if (user?.id) {
        const subscription = await subscribeToPushNotifications(user.id);
        if (subscription) {
          setIsSubscribed(true);
          toast.success("Subscrição de push ativada!");
        }
      }
      
      showNotification("EP3D MES", {
        body: "Notificações ativadas com sucesso!",
        tag: "test-notification"
      });
    } else {
      toast.error("Permissão de notificações negada");
    }
  };

  const handleTestNotification = () => {
    showNotification("Teste de Notificação", {
      body: "Esta é uma notificação de teste do EP3D MES",
      tag: "test-notification",
      data: { url: "/dashboard" }
    });
    toast.success("Notificação de teste enviada!");
  };

  const handleInstall = async () => {
    const installed = await promptInstall();
    if (installed) {
      toast.success("App instalada com sucesso!");
      setIsInstalled(true);
      setCanInstall(false);
    }
  };

  const handleRefreshSW = async () => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      toast.success("Service Worker atualizado!");
    }
  };

  return (
    <div className="space-y-6">
      {/* Estado da App */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Estado da Aplicação
          </CardTitle>
          <CardDescription>
            Configurações PWA e estado da ligação
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Estado Online/Offline */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              {isOnline ? (
                <Wifi className="h-5 w-5 text-green-500" />
              ) : (
                <WifiOff className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className="font-medium">Estado da Ligação</p>
                <p className="text-sm text-muted-foreground">
                  {isOnline ? "Ligado à internet" : "Sem ligação à internet"}
                </p>
              </div>
            </div>
            <Badge className={isOnline ? "bg-green-500" : "bg-red-500"}>
              {isOnline ? "Online" : "Offline"}
            </Badge>
          </div>
          
          {/* Service Worker */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              {swRegistered ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <div>
                <p className="font-medium">Service Worker</p>
                <p className="text-sm text-muted-foreground">
                  {swRegistered ? "Registado e ativo" : "Não registado"}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefreshSW}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Atualizar
            </Button>
          </div>
          
          {/* Instalação PWA */}
          <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
            <div className="flex items-center gap-3">
              <Download className="h-5 w-5 text-blue-500" />
              <div>
                <p className="font-medium">Instalação</p>
                <p className="text-sm text-muted-foreground">
                  {isInstalled 
                    ? "App instalada no dispositivo" 
                    : canInstall 
                      ? "Disponível para instalação"
                      : "A correr no browser"
                  }
                </p>
              </div>
            </div>
            {!isInstalled && canInstall && (
              <Button onClick={handleInstall}>
                <Download className="h-4 w-4 mr-2" />
                Instalar
              </Button>
            )}
            {isInstalled && (
              <Badge className="bg-green-500">Instalada</Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Notificações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notificações Push
          </CardTitle>
          <CardDescription>
            Receba alertas de produção, mensagens e atualizações
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!supportsNotifications() ? (
            <div className="p-4 bg-amber-100 text-amber-800 rounded-lg">
              <p className="font-medium">Notificações não suportadas</p>
              <p className="text-sm">O seu browser não suporta notificações push.</p>
            </div>
          ) : (
            <>
              {/* Estado da Permissão */}
              <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {notificationPermission === "granted" ? (
                    <Bell className="h-5 w-5 text-green-500" />
                  ) : notificationPermission === "denied" ? (
                    <BellOff className="h-5 w-5 text-red-500" />
                  ) : (
                    <Bell className="h-5 w-5 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium">Permissão</p>
                    <p className="text-sm text-muted-foreground">
                      {notificationPermission === "granted" 
                        ? "Notificações ativadas"
                        : notificationPermission === "denied"
                          ? "Notificações bloqueadas"
                          : "Permissão não solicitada"
                      }
                    </p>
                  </div>
                </div>
                
                {notificationPermission === "default" && (
                  <Button onClick={handleRequestPermission}>
                    <Bell className="h-4 w-4 mr-2" />
                    Ativar
                  </Button>
                )}
                
                {notificationPermission === "granted" && (
                  <Badge className="bg-green-500">Ativo</Badge>
                )}
                
                {notificationPermission === "denied" && (
                  <Badge variant="destructive">Bloqueado</Badge>
                )}
              </div>
              
              {/* Botão de Teste */}
              {notificationPermission === "granted" && (
                <Button variant="outline" onClick={handleTestNotification} className="w-full">
                  <Bell className="h-4 w-4 mr-2" />
                  Enviar Notificação de Teste
                </Button>
              )}
              
              {notificationPermission === "denied" && (
                <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                  <p className="font-medium mb-1">Notificações bloqueadas</p>
                  <p>Para ativar, vá às definições do browser e permita notificações para este site.</p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
